import React, { useState, useEffect, useContext } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserProvider, Contract, ethers } from 'ethers';
import { AuthContext } from '../AuthContext';

// Web3Market Contract ABI (Comprehensive RWA Marketplace)
const WEB3MARKET_ABI = [
  // Main trading functions
  "function buyShares(uint256 tokenId, uint256 shares) external",
  "function sellShares(uint256 tokenId, uint256 shares, uint256 pricePerShare) external",
  "function approveUSDC(uint256 amount) external",
  "function decreaseUSDCAllowance(uint256 amount) external",
  // Asset management functions
  "function tokenizeAsset(string memory category, string memory name, string memory description, string memory location, uint256 totalValue, uint256 totalShares, string memory metadataURI) external returns (uint256)",
  "function initializeAssets() external",
  // Dividend functions
  "function distributeDividends(uint256 tokenId, uint256 totalDividends) external",
  "function claimDividends(uint256 tokenId) external",
  // View functions
  "function getAsset(uint256 tokenId) external view returns (tuple(uint256 tokenId, string category, string name, string description, string location, uint256 totalValue, uint256 totalShares, uint256 availableShares, uint256 pricePerShare, address originalOwner, string metadataURI, bool isActive, uint256 createdAt))",
  "function getUserShares(address user, uint256 tokenId) external view returns (uint256)",
  "function getUserTransactions(address user) external view returns (tuple(address buyer, address seller, uint256 tokenId, uint256 shares, uint256 pricePerShare, uint256 totalCost, uint256 feeAmount, uint256 timestamp)[])",
  "function getShareholders(uint256 tokenId) external view returns (address[] memory)",
  "function getDividendPool(uint256 tokenId) external view returns (uint256)",
  // Contract constants
  "function REVENUE_WALLET() external view returns (address)",
  "function FEE_PERCENT() external view returns (uint256)",
  "function FEE_DENOMINATOR() external view returns (uint256)",
  "function USDC() external view returns (address)",
  "function nextTokenId() external view returns (uint256)",
  "function owner() external view returns (address)",
  // Events
  "event AssetTokenized(uint256 indexed tokenId, string category, string name, uint256 totalValue, address owner)",
  "event SharesPurchased(address indexed buyer, uint256 indexed tokenId, uint256 shares, uint256 totalCost, uint256 fee)",
  "event SharesSold(address indexed seller, uint256 indexed tokenId, uint256 shares, uint256 totalReceived, uint256 fee)",
  "event DividendDistributed(uint256 indexed tokenId, uint256 totalAmount, uint256 perShare)",
  "event DividendClaimed(address indexed user, uint256 indexed tokenId, uint256 amount)",
  "event USDCApproved(address indexed user, uint256 amount)",
  "event USDCAllowanceDecreased(address indexed user, uint256 amount)"
];

// USDC ABI (for allowance and balance checks)
const USDC_ABI = [
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function balanceOf(address account) view returns (uint256)",
  "function transfer(address to, uint256 amount) returns (bool)",
  "function transferFrom(address from, address to, uint256 amount) returns (bool)"
];

const WEB3MARKET_CONTRACT_ADDRESS = "0x4A0294bAf7a570b4d6A67514D6230BB63F038a32"; // Sepolia deployed address

const Web3Markets = () => {
  const { isAuthenticated, ensName, loginWithSIWE, isENSHolder } = useContext(AuthContext) || {};
  const [account, setAccount] = useState(null);
  const [web3marketContract, setWeb3marketContract] = useState(null);
  const [usdcContract, setUsdcContract] = useState(null);
  const [contractData, setContractData] = useState({
    feePercent: 0,
    feeDenominator: 0,
    revenueWallet: '',
    usdcAddress: '',
    nextTokenId: 0,
    totalAssets: 0,
  });

  // Smart contract state
  const [contractAssets, setContractAssets] = useState([]);
  const [userShares, setUserShares] = useState({});
  const [userTransactions, setUserTransactions] = useState([]);
  const [dividendPools, setDividendPools] = useState({});
  const [usdcAllowance, setUsdcAllowance] = useState('0');
  const [usdcBalance, setUsdcBalance] = useState('0');

  // Transaction state
  const [selectedTokenId, setSelectedTokenId] = useState(1);
  const [shareAmount, setShareAmount] = useState('');
  const [pricePerShare, setPricePerShare] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize contracts and load data
  useEffect(() => {
    const init = async () => {
      if (window.ethereum && isAuthenticated && isENSHolder) {
        try {
          const provider = new BrowserProvider(window.ethereum);
          const accounts = await provider.send("eth_requestAccounts", []);
          setAccount(accounts[0]);

          const network = await provider.getNetwork();
          if (network.chainId === 11155111n) { // Sepolia testnet
            const signer = await provider.getSigner();

            // Initialize Web3Market contract
            const web3marketContractInstance = new Contract(WEB3MARKET_CONTRACT_ADDRESS, WEB3MARKET_ABI, signer);
            setWeb3marketContract(web3marketContractInstance);

            // Load contract data
            const [feePercent, feeDenominator, revenueWallet, usdcAddress, nextTokenId] = await Promise.all([
              web3marketContractInstance.FEE_PERCENT(),
              web3marketContractInstance.FEE_DENOMINATOR(),
              web3marketContractInstance.REVENUE_WALLET(),
              web3marketContractInstance.USDC(),
              web3marketContractInstance.nextTokenId(),
            ]);

            setContractData({
              feePercent: Number(feePercent),
              feeDenominator: Number(feeDenominator),
              feePercentDisplay: (Number(feePercent) / Number(feeDenominator) * 100).toFixed(1),
              revenueWallet: revenueWallet,
              usdcAddress: usdcAddress,
              nextTokenId: Number(nextTokenId),
              totalAssets: 0,
            });

            // Initialize USDC contract
            const usdcContractInstance = new Contract(usdcAddress, USDC_ABI, signer);
            setUsdcContract(usdcContractInstance);

            // Load initial data
            await loadContractAssets(web3marketContractInstance);
            await loadUsdcData(usdcContractInstance, accounts[0]);

          } else {
            setError("Please connect to Sepolia testnet");
          }
        } catch (err) {
          setError("Failed to initialize contract: " + err.message);
          console.error(err);
        }
      }
    };
    init();
  }, [isAuthenticated, isENSHolder]);

  // Load contract assets
  const loadContractAssets = async (contractInstance = web3marketContract) => {
    if (!contractInstance) return;
    try {
      const nextTokenId = await contractInstance.nextTokenId();
      const assets = [];

      for (let tokenId = 1; tokenId < nextTokenId; tokenId++) {
        try {
          const asset = await contractInstance.getAsset(tokenId);
          const formattedAsset = {
            tokenId: Number(asset.tokenId),
            category: asset.category,
            name: asset.name,
            description: asset.description,
            location: asset.location,
            totalValue: ethers.formatUnits(asset.totalValue, 6),
            totalShares: Number(asset.totalShares),
            availableShares: Number(asset.availableShares),
            pricePerShare: ethers.formatUnits(asset.pricePerShare, 6),
            originalOwner: asset.originalOwner,
            isActive: asset.isActive,
            createdAt: Number(asset.createdAt),
          };
          assets.push(formattedAsset);
        } catch (err) {
          console.log(`Asset ${tokenId} not found, stopping iteration`);
          break;
        }
      }

      setContractAssets(assets);
      setContractData(prev => ({ ...prev, totalAssets: assets.length }));

      // Load user-specific data
      if (account) {
        await loadUserShares(contractInstance, assets);
        await loadUserTransactions(contractInstance);
        await loadDividendPools(contractInstance, assets);
      }
    } catch (error) {
      console.error('Failed to load contract assets:', error);
      setError('Failed to load assets from contract');
    }
  };

  // Load USDC data
  const loadUsdcData = async (usdcInstance = usdcContract, userAddress = account) => {
    if (!usdcInstance || !userAddress) return;
    try {
      const [allowance, balance] = await Promise.all([
        usdcInstance.allowance(userAddress, WEB3MARKET_CONTRACT_ADDRESS),
        usdcInstance.balanceOf(userAddress)
      ]);

      setUsdcAllowance(ethers.formatUnits(allowance, 6));
      setUsdcBalance(ethers.formatUnits(balance, 6));
    } catch (error) {
      console.error('Failed to load USDC data:', error);
    }
  };

  // Load user shares
  const loadUserShares = async (contractInstance = web3marketContract, assets = contractAssets) => {
    if (!contractInstance || !account || !assets.length) return;
    try {
      const shares = {};
      for (const asset of assets) {
        const userShareAmount = await contractInstance.getUserShares(account, asset.tokenId);
        shares[asset.tokenId] = Number(userShareAmount);
      }
      setUserShares(shares);
    } catch (error) {
      console.error('Failed to load user shares:', error);
    }
  };

  // Load user transactions
  const loadUserTransactions = async (contractInstance = web3marketContract) => {
    if (!contractInstance || !account) return;
    try {
      const transactions = await contractInstance.getUserTransactions(account);
      const formattedTransactions = transactions.map((tx, index) => ({
        id: index,
        buyer: tx.buyer,
        seller: tx.seller,
        tokenId: Number(tx.tokenId),
        shares: Number(tx.shares),
        pricePerShare: ethers.formatUnits(tx.pricePerShare, 6),
        totalCost: ethers.formatUnits(tx.totalCost, 6),
        feeAmount: ethers.formatUnits(tx.feeAmount, 6),
        timestamp: Number(tx.timestamp),
      }));
      setUserTransactions(formattedTransactions);
    } catch (error) {
      console.error('Failed to load user transactions:', error);
    }
  };

  // Load dividend pools
  const loadDividendPools = async (contractInstance = web3marketContract, assets = contractAssets) => {
    if (!contractInstance || !assets.length) return;
    try {
      const pools = {};
      for (const asset of assets) {
        const pool = await contractInstance.getDividendPool(asset.tokenId);
        pools[asset.tokenId] = ethers.formatUnits(pool, 6);
      }
      setDividendPools(pools);
    } catch (error) {
      console.error('Failed to load dividend pools:', error);
    }
  };

  // USDC Approval
  const handleApproveUSDC = async () => {
    if (!usdcContract || !shareAmount || !selectedTokenId) return;
    setLoading(true);
    setError(null);

    try {
      const selectedAsset = contractAssets.find(asset => asset.tokenId === selectedTokenId);
      if (!selectedAsset) {
        throw new Error('Selected asset not found');
      }

      const shares = Number(shareAmount);
      const grossCost = shares * parseFloat(selectedAsset.pricePerShare);
      const feeAmount = (grossCost * contractData.feePercent) / contractData.feeDenominator;
      const totalCost = grossCost + feeAmount;
      const totalCostWei = ethers.parseUnits(totalCost.toFixed(6), 6);

      const tx = await usdcContract.approve(WEB3MARKET_CONTRACT_ADDRESS, totalCostWei);
      await tx.wait();

      // Reload USDC data
      await loadUsdcData();

      alert(`USDC approval successful! Approved ${totalCost.toFixed(2)} USDC`);
    } catch (error) {
      console.error('USDC approval error:', error);
      setError(`USDC approval failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Buy Shares
  const handleBuyShares = async () => {
    if (!web3marketContract || !selectedTokenId || !shareAmount) {
      setError("Please select an asset and enter share amount");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const shares = Number(shareAmount);

      // Check if we have sufficient allowance
      const requiredAmount = await calculateRequiredUSDC(shares);
      if (parseFloat(usdcAllowance) < requiredAmount) {
        setError(`Insufficient USDC allowance. Please approve at least ${requiredAmount} USDC first.`);
        return;
      }

      const tx = await web3marketContract.buyShares(selectedTokenId, shares);
      alert(`Buy order submitted! Transaction: ${tx.hash}`);

      const receipt = await tx.wait();
      alert(`Purchase confirmed! Bought ${shares} shares of asset #${selectedTokenId}`);

      // Refresh all data
      await Promise.all([
        loadContractAssets(),
        loadUsdcData(),
        loadUserShares(),
        loadUserTransactions(),
        loadDividendPools()
      ]);

    } catch (error) {
      console.error('Buy shares error:', error);
      setError(`Buy failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Sell Shares
  const handleSellShares = async () => {
    if (!web3marketContract || !selectedTokenId || !shareAmount || !pricePerShare) {
      setError("Please fill all fields");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const shares = Number(shareAmount);
      const price = ethers.parseUnits(pricePerShare, 6);

      // Check if user has enough shares
      const userShareAmount = userShares[selectedTokenId] || 0;
      if (userShareAmount < shares) {
        setError(`Insufficient shares. You own ${userShareAmount} shares.`);
        return;
      }

      const tx = await web3marketContract.sellShares(selectedTokenId, shares, price);
      alert(`Sell order submitted! Transaction: ${tx.hash}`);

      await tx.wait();
      alert(`Sale confirmed! Sold ${shares} shares at $${pricePerShare} each`);

      // Refresh all data
      await Promise.all([
        loadContractAssets(),
        loadUserShares(),
        loadUserTransactions(),
        loadDividendPools()
      ]);

    } catch (error) {
      console.error('Sell shares error:', error);
      setError(`Sell failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Claim Dividends
  const handleClaimDividends = async (tokenId) => {
    if (!web3marketContract) return;

    setLoading(true);
    setError(null);

    try {
      const tx = await web3marketContract.claimDividends(tokenId);
      alert(`Dividend claim submitted! Transaction: ${tx.hash}`);

      await tx.wait();
      alert(`Dividends claimed successfully for asset #${tokenId}!`);

      // Refresh data
      await Promise.all([
        loadDividendPools(),
        loadUserShares()
      ]);

    } catch (error) {
      console.error('Dividend claim error:', error);
      setError(`Dividend claim failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Calculate required USDC for purchase
  const calculateRequiredUSDC = async (shares) => {
    if (!web3marketContract || !selectedTokenId) return 0;

    try {
      const asset = await web3marketContract.getAsset(selectedTokenId);
      const grossCost = shares * Number(ethers.formatUnits(asset.pricePerShare, 6));
      const feeAmount = (grossCost * contractData.feePercent) / contractData.feeDenominator;
      return grossCost + feeAmount;
    } catch (error) {
      console.error('Error calculating required USDC:', error);
      return 0;
    }
  };

  // Calculate purchase details
  const calculatePurchaseDetails = () => {
    if (!shareAmount || !selectedTokenId) return null;

    const selectedAsset = contractAssets.find(asset => asset.tokenId === selectedTokenId);
    if (!selectedAsset) return null;

    const shares = Number(shareAmount);
    const grossCost = shares * parseFloat(selectedAsset.pricePerShare);
    const feeAmount = (grossCost * contractData.feePercent) / contractData.feeDenominator;
    const totalCost = grossCost + feeAmount;

    return {
      grossCost: grossCost.toFixed(2),
      feeAmount: feeAmount.toFixed(2),
      totalCost: totalCost.toFixed(2),
      shares,
      pricePerShare: selectedAsset.pricePerShare
    };
  };

  if (!isAuthenticated || !isENSHolder) {
    return (
      <div style={{ textAlign: 'center', color: 'gold', marginTop: '20px' }}>
        <p>Please mint your .xbsys.eth subdomain to access Web3Markets.</p>
        <button
          onClick={loginWithSIWE}
          style={{
            background: 'transparent',
            border: '2px solid gold',
            color: 'gold',
            padding: '15px 30px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
          }}
        >
          Connect Wallet
        </button>
      </div>
    );
  }

  const selectedAsset = contractAssets.find(asset => asset.tokenId === selectedTokenId);
  const purchaseDetails = calculatePurchaseDetails();

  return (
    <section className="web3markets-container">
      <Helmet>
        <title>Web3Markets.eth - RWA Marketplace | BankSystem</title>
      </Helmet>

      <div style={{
        background: 'black',
        padding: '20px',
        borderRadius: '10px',
        boxShadow: '0 0 20px gold',
        maxWidth: '1200px',
        margin: '20px auto',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{ color: 'gold', fontSize: '32px', fontWeight: 'bold', marginBottom: '10px' }}>
            Web3Markets.eth - RWA Marketplace
          </h1>
          <p style={{ color: 'gold', fontSize: '16px', marginBottom: '20px' }}>
            Tokenize and trade Real World Assets with {contractData.feePercentDisplay || '0.5'}% revenue fees
          </p>
        </div>

        {/* Connection Status */}
        <div style={{
          backgroundColor: '#333',
          padding: '1rem',
          borderRadius: '5px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: isAuthenticated ? '#00FF00' : '#FF0000',
            }}></div>
            <span style={{ fontWeight: 'bold', color: isAuthenticated ? '#00FF00' : '#FF0000' }}>
              {isAuthenticated ? 'CONNECTED' : 'DISCONNECTED'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: 'gold', fontSize: '14px' }}>
              {ensName ? `${ensName}` : account ? `${account.substring(0, 6)}...${account.substring(account.length - 4)}` : 'Not connected'}
            </span>
          </div>
        </div>

        {/* USDC Balance and Allowance */}
        <div style={{
          backgroundColor: '#333',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '15px',
        }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: 'gold', margin: '0', fontSize: '14px' }}>USDC Balance</p>
            <p style={{ color: 'gold', margin: '5px 0 0 0', fontSize: '18px', fontWeight: 'bold' }}>
              ${usdcBalance}
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: 'gold', margin: '0', fontSize: '14px' }}>USDC Allowance</p>
            <p style={{ color: 'gold', margin: '5px 0 0 0', fontSize: '18px', fontWeight: 'bold' }}>
              ${usdcAllowance}
            </p>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            color: 'red',
            marginBottom: '20px',
            textAlign: 'center',
            background: 'rgba(255,0,0,0.1)',
            padding: '10px',
            borderRadius: '5px'
          }}>
            {error}
          </div>
        )}

        {/* Asset Selection */}
        <div style={{
          backgroundColor: '#333',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h4 style={{ color: 'gold', marginBottom: '10px' }}>Select Asset</h4>
          {contractAssets.length === 0 ? (
            <p style={{ color: 'gold', textAlign: 'center' }}>Loading assets from contract...</p>
          ) : (
            <select
              value={selectedTokenId}
              onChange={(e) => setSelectedTokenId(Number(e.target.value))}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#222',
                color: 'gold',
                border: '1px solid gold',
                borderRadius: '3px',
              }}
            >
              {contractAssets.map(asset => (
                <option key={asset.tokenId} value={asset.tokenId}>
                  #{asset.tokenId} - {asset.name} ({asset.category}) - ${asset.pricePerShare}/share
                </option>
              ))}
            </select>
          )}

          {/* Asset Details */}
          {selectedAsset && (
            <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(255,215,0,0.1)', borderRadius: '3px' }}>
              <p style={{ color: 'gold', margin: '5px 0' }}><strong>Asset:</strong> {selectedAsset.name}</p>
              <p style={{ color: 'gold', margin: '5px 0' }}><strong>Category:</strong> {selectedAsset.category}</p>
              <p style={{ color: 'gold', margin: '5px 0' }}><strong>Location:</strong> {selectedAsset.location}</p>
              <p style={{ color: 'gold', margin: '5px 0' }}><strong>Total Value:</strong> ${selectedAsset.totalValue}</p>
              <p style={{ color: 'gold', margin: '5px 0' }}><strong>Available Shares:</strong> {selectedAsset.availableShares}</p>
              <p style={{ color: 'gold', margin: '5px 0' }}><strong>Price per Share:</strong> ${selectedAsset.pricePerShare}</p>
              <p style={{ color: 'gold', margin: '5px 0' }}><strong>Your Shares:</strong> {userShares[selectedAsset.tokenId] || 0}</p>
              <p style={{ color: 'gold', margin: '5px 0' }}><strong>Dividend Pool:</strong> ${dividendPools[selectedAsset.tokenId] || '0.00'}</p>
            </div>
          )}
        </div>

        {/* Trading Interface */}
        <div style={{
          backgroundColor: '#333',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px',
        }}>
          {/* Buy Shares */}
          <div>
            <h4 style={{ color: 'gold', marginBottom: '10px' }}>Buy Shares</h4>
            <input
              type="number"
              placeholder="Number of shares"
              value={shareAmount}
              onChange={(e) => setShareAmount(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '10px',
                backgroundColor: '#222',
                color: 'gold',
                border: '1px solid gold',
                borderRadius: '3px',
              }}
            />

            {purchaseDetails && (
              <div style={{
                background: 'rgba(255,215,0,0.1)',
                padding: '10px',
                borderRadius: '3px',
                marginBottom: '10px',
                fontSize: '12px'
              }}>
                <p style={{ color: 'gold', margin: '2px 0' }}>Gross Cost: ${purchaseDetails.grossCost}</p>
                <p style={{ color: 'gold', margin: '2px 0' }}>Fee (0.5%): ${purchaseDetails.feeAmount}</p>
                <p style={{ color: 'gold', margin: '2px 0', fontWeight: 'bold' }}>Total: ${purchaseDetails.totalCost}</p>
              </div>
            )}

            <button
              onClick={handleApproveUSDC}
              disabled={loading || !selectedTokenId || !shareAmount}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '10px',
                backgroundColor: loading ? '#666' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Approving...' : 'Approve USDC'}
            </button>

            <button
              onClick={handleBuyShares}
              disabled={loading || !selectedTokenId || !shareAmount}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: loading ? '#666' : 'gold',
                color: 'black',
                border: 'none',
                borderRadius: '3px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Processing...' : 'Buy Shares'}
            </button>
          </div>

          {/* Sell Shares */}
          <div>
            <h4 style={{ color: 'gold', marginBottom: '10px' }}>Sell Shares</h4>
            <input
              type="number"
              placeholder="Number of shares"
              value={shareAmount}
              onChange={(e) => setShareAmount(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '10px',
                backgroundColor: '#222',
                color: 'gold',
                border: '1px solid gold',
                borderRadius: '3px',
              }}
            />
            <input
              type="number"
              placeholder="Price per share ($)"
              value={pricePerShare}
              onChange={(e) => setPricePerShare(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '10px',
                backgroundColor: '#222',
                color: 'gold',
                border: '1px solid gold',
                borderRadius: '3px',
              }}
            />
            <button
              onClick={handleSellShares}
              disabled={loading || !selectedTokenId || !shareAmount || !pricePerShare}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: loading ? '#666' : '#ff6b35',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Processing...' : 'Sell Shares'}
            </button>
          </div>
        </div>

        {/* Dividends Section */}
        {contractAssets.length > 0 && (
          <div style={{
            backgroundColor: '#333',
            padding: '15px',
            borderRadius: '5px',
            marginBottom: '20px',
          }}>
            <h4 style={{ color: 'gold', marginBottom: '15px' }}>Claim Dividends</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px' }}>
              {contractAssets.map(asset => (
                <div key={asset.tokenId} style={{
                  background: 'rgba(255,215,0,0.1)',
                  padding: '10px',
                  borderRadius: '3px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <div>
                    <p style={{ color: 'gold', margin: '0 0 5px 0', fontSize: '14px' }}>{asset.name}</p>
                    <p style={{ color: 'gold', margin: '0', fontSize: '12px' }}>Pool: ${dividendPools[asset.tokenId] || '0.00'}</p>
                    <p style={{ color: 'gold', margin: '0', fontSize: '12px' }}>Your Shares: {userShares[asset.tokenId] || 0}</p>
                  </div>
                  <button
                    onClick={() => handleClaimDividends(asset.tokenId)}
                    disabled={loading || !userShares[asset.tokenId]}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: loading || !userShares[asset.tokenId] ? '#666' : '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      fontSize: '12px',
                      cursor: loading || !userShares[asset.tokenId] ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Claim
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Transaction History */}
        {userTransactions.length > 0 && (
          <div style={{
            backgroundColor: '#333',
            padding: '15px',
            borderRadius: '5px',
            marginBottom: '20px',
          }}>
            <h4 style={{ color: 'gold', marginBottom: '15px' }}>Your Transaction History</h4>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {userTransactions.map((tx, index) => (
                <div key={index} style={{
                  background: 'rgba(255,215,0,0.1)',
                  padding: '10px',
                  marginBottom: '10px',
                  borderRadius: '3px',
                }}>
                  <p style={{ color: 'gold', margin: '0 0 5px 0', fontSize: '14px' }}>
                    Asset #{tx.tokenId} - {tx.shares} shares @ ${tx.pricePerShare}
                  </p>
                  <p style={{ color: 'gold', margin: '0 0 5px 0', fontSize: '12px' }}>
                    Total: ${tx.totalCost} | Fee: ${tx.feeAmount}
                  </p>
                  <p style={{ color: 'gold', margin: '0', fontSize: '11px' }}>
                    {new Date(tx.timestamp * 1000).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contract Info */}
        <div style={{
          backgroundColor: '#333',
          padding: '15px',
          borderRadius: '5px',
          textAlign: 'center',
        }}>
          <h4 style={{ color: 'gold', marginBottom: '10px' }}>Contract Information</h4>
          <p style={{ color: 'gold', margin: '5px 0', fontSize: '12px' }}>
            Contract: {WEB3MARKET_CONTRACT_ADDRESS}
          </p>
          <p style={{ color: 'gold', margin: '5px 0', fontSize: '12px' }}>
            Trading Fee: {contractData.feePercentDisplay || '0.5'}% | USDC: {contractData.usdcAddress?.substring(0, 10)}...
          </p>
          <p style={{ color: 'gold', margin: '5px 0', fontSize: '12px' }}>
            Revenue Wallet: {contractData.revenueWallet?.substring(0, 10)}...
          </p>
          <p style={{ color: 'gold', margin: '5px 0', fontSize: '12px' }}>
            Total Assets: {contractData.totalAssets}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Web3Markets;
